import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import Header from './Header';
import Footer from './Footer';
import ContactForm from './ContactForm';

function Contact() {
  return (
    <div className="bg-gray-50 min-h-screen overflow-x-hidden">
      <Helmet>
        <title>Contact Advanced Controls & Automation | Precision CNC Machining in Waterford, NY</title>
        <meta
          name="description"
          content="Contact Advanced Controls & Automation for precision CNC machining, tungsten carbide HVAF coatings, and automation systems. Based in Waterford, NY, serving the Capital District and Upstate New York."
        />
        <meta
          name="keywords"
          content="contact precision CNC machining, tungsten carbide coatings, automation systems, Waterford NY, Capital District, Upstate New York, American manufacturing, HVAF coatings"
        />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.acamachine.com/contact" />
        <meta property="og:title" content="Contact Advanced Controls & Automation" />
        <meta property="og:description" content="Reach out to Advanced Controls & Automation for precision CNC machining, tungsten carbide HVAF coatings, and automation systems in Waterford, NY." />
        <meta property="og:image" content="https://www.acamachine.com/preview.jpg" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://www.acamachine.com/contact" />
        <meta property="twitter:title" content="Contact Advanced Controls & Automation" />
        <meta property="twitter:description" content="Reach out to Advanced Controls & Automation for precision CNC machining, tungsten carbide HVAF coatings, and automation systems in Waterford, NY." />
        <meta property="twitter:image" content="https://www.acamachine.com/preview.jpg" />
        <link rel="canonical" href="https://www.acamachine.com/contact" />
      </Helmet>

      <Header />

      {/* Hero Section */}
      <section
        className="pt-36 pb-16"
        style={{
          backgroundColor: '#fff',
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='52' viewBox='0 0 60 52'%3E%3Cpath d='M30 0 L60 52 L0 52 Z' fill='none' stroke='rgba(0,0,0,0.05)' stroke-width='1'/%3E%3Cpath d='M0 0 L30 52 L60 0' fill='none' stroke='rgba(0,0,0,0.05)' stroke-width='1'/%3E%3C/svg%3E")`,
          backgroundSize: '60px 52px',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6"
          >
            Contact Us
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-8"
          >
            Have questions about our <strong>precision CNC machining</strong>, <strong>tungsten carbide HVAF coatings</strong>, or <strong>automation systems</strong>? Reach out to our team in <strong>Waterford, NY</strong>. We proudly serve the <strong>Capital District</strong>, <strong>Upstate New York</strong>, and beyond with <strong>American-made</strong> solutions.
          </motion.p>
        </div>
      </section>

      {/* Contact Information Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">
            Our Contact Details
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
            <div className="flex items-center justify-center">
              <svg
                className="w-6 h-6 text-blue-600 mr-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <p className="text-gray-600">
                574 Hudson River Road, Waterford, NY 12188
              </p>
            </div>
            <div className="flex items-center justify-center">
              <svg
                className="w-6 h-6 text-blue-600 mr-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              <a
                href="mailto:sales@acamachine.com"
                className="text-gray-600 hover:text-blue-600 transition duration-200"
              >
                sales@acamachine.com
              </a>
            </div>
            <div className="flex items-center justify-center">
              <svg
                className="w-6 h-6 text-blue-600 mr-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
              <a
                href="tel:+15189530407"
                className="text-gray-600 hover:text-blue-600 transition duration-200"
              >
                +1 (518) 953-0407
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <ContactForm />

      <Footer />
    </div>
  );
}

export default Contact;